import { IconApple, IconBrandAndroid, IconBrandApple } from "@tabler/icons-react";
import { QRCode } from "antd";
import { Globe, ArrowUpRight, Sparkles, Play } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const apps = [
  {
    name: "SIRE 2.0 Prep",
    description: "A web-based tool to review and understand the full SIRE 2.0 question set. Ideal for office teams to prepare and train before inspections.",
    ios: "https://apps.apple.com/app/id6478749472",
    android: "https://play.google.com/store/apps/details?id=com.sirefrontend",
    web: "https://sire.solmarinetech.com/demo",
    gradient: "from-violet-600 via-purple-600 to-blue-600",
    accentColor: "violet",
    logo: "https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/37/15/08/37150809-f267-5953-1dc1-df640b922f18/AppIcon-0-0-1x_U007emarketing-0-11-0-85-220.png/460x0w.webp"
  },
  {
    name: "Inspector – SIRE 2.0 Prep",
    description: "A mobile app for vessel crews to run mock inspections with offline access. Capture responses, add comments, and sync data when online.",
    ios: "https://apps.apple.com/app/id6748201188",
    android: "https://play.google.com/store/apps/details?id=com.siresolmarineinspectors",
    web: "https://s2insp.solmarinetech.com/demo",
    gradient: "from-violet-600 via-purple-600 to-blue-600",
    accentColor: "violet",
    logo: "https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/99/84/c5/9984c5d9-daea-541a-f623-c549af938d2f/AppIcon-0-0-1x_U007emarketing-0-11-0-85-220.png/460x0w.webp"
  },
  {
    name: "SIRE 2.0 Analytics",
    description: "Analyze inspection trends, findings, and chapter-wise performance. Built for technical superintendents and managers.",
    ios: "",
    android: "",
    web: "https://sire2analysis.solmarinetech.com/trial",
    gradient: "from-blue-600 via-cyan-600 to-teal-600",
    accentColor: "blue",
  },
  {
    name: "Vessel Inspection Reporting (VIR)",
    description: "Conduct inspections using RISQ, ISM, or OCIMF frameworks. Record responses, comments, and evidence — mobile and web-ready.",
    ios: "https://apps.apple.com/app/id6738503486",
    android: "https://play.google.com/store/apps/details?id=com.virsolmarine",
    web: "https://vir.solmarinetech.com",
    gradient: "from-pink-600 via-rose-600 to-orange-600",
    accentColor: "pink",
    logo: "https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/be/33/81/be3381f6-21f4-bcc2-11ae-d7a37a5cbd0d/AppIcon-0-0-1x_U007emarketing-0-11-0-85-220.png/460x0w.webp"
  }
];

const PlatformLink = ({ url, icon: Icon, children, variant = "primary" }) => {
  if (!url) return null;
  
  const variants = {
    primary: "bg-white text-gray-900 hover:bg-gray-50 shadow-xl hover:shadow-2xl",
    secondary: "bg-white/10 text-white hover:bg-white/20 backdrop-blur-lg border border-white/20",
    accent: "bg-gradient-to-r from-white/20 to-white/10 text-white hover:from-white/30 hover:to-white/20 backdrop-blur-lg border border-white/30"
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-semibold transition-all duration-500 hover:scale-[1.02] ${variants[variant]} overflow-hidden`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
      <Icon className="w-5 h-5 relative z-10" />
      <span className="relative z-10">{children}</span>
      <ArrowUpRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity relative z-10" />
    </a>
  );
};

const AppCard = ({ app, index }) => {
  const [showQR, setShowQR] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
    className="group relative bg-black/40 backdrop-blur-2xl rounded-[2rem] border border-white/10 overflow-hidden transition-all duration-700 hover:border-white/20 hover:bg-black/30"
    onMouseEnter={() => setIsHovered(true)}
    onMouseLeave={() => setIsHovered(false)}
    style={{
    animationDelay: `${index * 200}ms`,
    animation: 'fadeInScale 1s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    opacity: 0,
    transform: 'scale(0.8) translateY(40px)'
    }}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${app.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-700`} />
      
      <div className={`absolute inset-0 rounded-[2rem] bg-gradient-to-br ${app.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-700`} />

      <div className="relative p-10">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${app.gradient} flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform duration-500`}>
              {
                app?.logo ?
                <img src={app.logo} className="rounded-2xl" /> :
                <Sparkles className="w-8 h-8 text-white" />
              }
            </div>
            <div className="flex-1">
              <h3 
              className="text-2xl font-bold text-white mb-2 group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:text-transparent transition-all duration-500"
              style={{
                backgroundImage: isHovered
                  ? app.accentColor === 'violet'
                    ? 'linear-gradient(to right, #8b5cf6, #3b82f6)'
                    : app.accentColor === 'blue'
                    ? 'linear-gradient(to right, #3b82f6, #06b6d4)'
                    : app.accentColor === 'pink'
                    ? 'linear-gradient(to right, #ec4899, #f97316)'
                    : 'linear-gradient(to right, #10b981, #06b6d4)'
                  : 'none',
              }}
              >
                {app.name}
              </h3>
            </div>
          </div>
          
          <p className="text-gray-300 text-lg leading-relaxed">
            {app.description}
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <PlatformLink url={app.ios} icon={IconBrandApple} variant="primary">
            App Store
          </PlatformLink>
          <PlatformLink url={app.android} icon={IconBrandAndroid} variant="secondary">
            Google Play
          </PlatformLink>
          <PlatformLink url={app.web} icon={Globe} variant="accent">
            Trial Access
          </PlatformLink>
        </div>

        {(app.ios || app.android) && (
          <div className="border-t border-white/10 pt-8">
            <button
            onClick={() => setShowQR(!showQR)}
            className="w-full group/btn flex items-center justify-between p-5 bg-white/5 hover:bg-white/10 rounded-2xl transition-all duration-300 backdrop-blur-sm border border-white/10 hover:border-white/20"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${app.gradient} flex items-center justify-center`}>
                  <Play className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-white">Quick Access Codes</span>
              </div>
              <div className={`transform transition-all duration-500 ${showQR ? 'rotate-180' : ''}`}>
                <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover/btn:text-white" />
              </div>
            </button>
            
            {showQR && (
              <div className="mt-6 p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 transform transition-all duration-500 animate-slideDown">
                <div className="flex justify-center gap-12">
                  {app.ios && (
                    <div className="text-center group/qr">
                      <div className="p-4 bg-white rounded-2xl shadow-2xl group-hover/qr:shadow-3xl transition-shadow duration-300 transform group-hover/qr:scale-105">
                        <QRCode value={app.ios} size={120} />
                      </div>
                      <p className="text-sm font-medium text-gray-300 mt-4">iOS App</p>
                    </div>
                  )}
                  {app.android && (
                    <div className="text-center group/qr">
                      <div className="p-4 bg-white rounded-2xl shadow-2xl group-hover/qr:shadow-3xl transition-shadow duration-300 transform group-hover/qr:scale-105">
                        <QRCode value={app.android} size={120} />
                      </div>
                      <p className="text-sm font-medium text-gray-300 mt-4">Android App</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
        <div className="absolute top-0 left-1/2 w-32 h-32 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
      </div>
    </div>
  );
};

const ExploreProductsScreen = () => {

  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-violet-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse" 
             style={{animationDuration: '4s'}} />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse" 
             style={{animationDuration: '6s', animationDelay: '2s'}} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-pink-600/20 to-rose-600/20 rounded-full blur-3xl animate-pulse transform -translate-x-1/2 -translate-y-1/2" 
             style={{animationDuration: '5s', animationDelay: '1s'}} />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="font-semibold text-lg">
              <img src={require('../../assets/images/logo-white.png')} alt="SolMarine Tech" className="w-[140px]" />
            </span>
          </div>
          
          <h1 className="flex justify-center text-4xl font-black text-white mb-6 leading-tight tracking-tight gap-4 font-montserrat">
            <div className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              MARITIME
            </div>
            <div className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
              SOLUTIONS
            </div>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
            Discover the future of maritime operations with our sophisticated suite of 
            professional applications crafted for industry excellence.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          {apps.map((app, index) => (
            <AppCard key={index} app={app} index={index} />
          ))}
        </div>
        
        <div className="text-center">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/20 rounded-3xl p-16 max-w-5xl mx-auto shadow-2xl">
            <h3 className="text-4xl font-bold text-white mb-8 leading-tight">
              Transform Your Maritime Operations
            </h3>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
              Experience unparalleled efficiency and precision with Solmarine's cutting-edge technology 
              trusted by maritime professionals worldwide.
            </p>
            <div className="flex gap-4 justify-center">
              <button 
              onClick={()=> navigate(-1)}
              className="group relative bg-gradient-to-r from-emerald-600 via-emerald-600 to-orange-600 text-white px-12 py-6 rounded-2xl font-bold text-xl transition-all duration-500 hover:scale-105 shadow-2xl hover:shadow-violet-500/25 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                <span className="relative z-10">Back to Application</span>
              </button>
              <button 
              onClick={()=> window.open("https://solmarinetech.com/")}
              className="group relative bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 text-white px-12 py-6 rounded-2xl font-bold text-xl transition-all duration-500 hover:scale-105 shadow-2xl hover:shadow-violet-500/25 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                <span className="relative z-10">Explore Solutions</span>
              </button>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default ExploreProductsScreen;